import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { loginInputSchema, type LoginInput, type LoginResponse } from '@timesheet/shared';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Field } from '@/components/ui/Field';
import { Alert } from '@/components/ui/Alert';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthed, login } = useAuth();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginInputSchema),
    defaultValues: { username: '', password: '' },
  });

  // If already logged in, bounce to dashboard (or wherever they came from).
  useEffect(() => {
    if (isAuthed) {
      const from = (location.state as LocationState | undefined)?.from ?? '/';
      navigate(from, { replace: true });
    }
  }, [isAuthed, location.state, navigate]);

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    const res = await api<LoginResponse>('/auth/login', {
      method: 'POST',
      json: values,
      skipAuth: true,
    });
    if (!res.ok) {
      setSubmitError(res.error.message);
      return;
    }
    login(res.data);
    const from = (location.state as LocationState | undefined)?.from ?? '/';
    navigate(from, { replace: true });
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-off-white px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center">
          <Logo height={56} />
        </div>

        <div className="rounded-lg border border-brand-gray-light bg-white p-7 shadow-card">
          <h1 className="text-center text-lg font-semibold text-brand-charcoal">
            Sign in to Timesheet
          </h1>
          <p className="mt-1 text-center text-sm text-brand-gray-mid">
            Use the credentials provided by your admin
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
            <Field label="Username" error={errors.username?.message} required>
              {(p) => (
                <Input
                  {...p}
                  type="text"
                  autoComplete="username"
                  autoFocus
                  placeholder="admin"
                  {...register('username')}
                />
              )}
            </Field>

            <Field label="Password" error={errors.password?.message} required>
              {(p) => (
                <Input
                  {...p}
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  {...register('password')}
                />
              )}
            </Field>

            {submitError && (
              <Alert tone="danger" title="Sign-in failed">
                {submitError}
              </Alert>
            )}

            <Button type="submit" loading={isSubmitting} fullWidth size="lg">
              {isSubmitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-brand-gray-mid">
          © SIAM TRUTH AUDIT COMPANY LIMITED
        </p>
      </div>
    </div>
  );
}
